import { useState } from "react";
import { useCanAccess, useTranslate, type Identifier } from "ra-core";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { CONTACT_EXPEDIENTE_RESOURCE } from "../../providers/commons/canAccessContactExpediente";
import {
  EXPEDIENTE_PERSON_KINDS,
  EXPEDIENTE_WRITABLE_FIELDS,
  PERSON_KIND_LABELS,
} from "../../providers/commons/contactExpediente";
import type {
  Contact,
  ContactExpedienteBundle,
  ContactExpedienteInput,
  ExpedientePersonKind,
} from "../../types";
import {
  ExpedienteChecklist,
  ExpedienteRestrictedView,
} from "./ExpedienteChecklist";
import { ExpedienteDocuments } from "./ExpedienteDocuments";
import { ExpedienteRadioGroup } from "./ExpedienteField";
import { ExpedientePersonaFisicaFields } from "./ExpedientePersonaFisicaFields";
import { ExpedientePersonaMoralFields } from "./ExpedientePersonaMoralFields";
import { ExpedientePldFields } from "./ExpedientePldFields";
import {
  buildExpedienteSavePayload,
  computeExpedienteProgress,
  validateExpedienteForm,
} from "./expedienteFormRules";
import {
  isExpedienteDenied,
  useContactExpediente,
} from "./useContactExpediente";

/** The writable columns of a stored expediente, as the form's starting draft. */
const draftFrom = (bundle: ContactExpedienteBundle): ContactExpedienteInput =>
  Object.fromEntries(
    EXPEDIENTE_WRITABLE_FIELDS.filter(
      (field) => bundle.expediente?.[field] != null,
    ).map((field) => [field, bundle.expediente?.[field]]),
  ) as ContactExpedienteInput;

const TabSkeleton = () => {
  const translate = useTranslate();
  return (
    <div role="status" aria-busy="true" className="space-y-4">
      <span className="sr-only">
        {translate("crm.contact_expediente.loading")}
      </span>
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
};

/**
 * The Expediente form. The draft lives only in this component's state (never in localStorage, a
 * store or a query key); an explicit "Guardar" saves partially and shows "Guardado".
 */
const ExpedienteForm = ({
  contactId,
  bundle,
  api,
}: {
  contactId: Identifier;
  bundle: ContactExpedienteBundle;
  api: ReturnType<typeof useContactExpediente>;
}) => {
  const translate = useTranslate();
  const [draft, setDraft] = useState<ContactExpedienteInput>(() =>
    draftFrom(bundle),
  );
  const [attempted, setAttempted] = useState(false);
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const { errors, warnings } = validateExpedienteForm(draft, attempted, {
    kind: translate("crm.contact_expediente.kind_required"),
    regime: translate("crm.contact_expediente.regime_required"),
    ownerName: translate("crm.contact_expediente.owner_name_required"),
    idExpired: translate("crm.contact_expediente.id_expired"),
  });
  const messages = { ...warnings, ...errors };
  const progress = computeExpedienteProgress(
    bundle.expediente,
    bundle.documents,
  );

  const onChange: Parameters<typeof ExpedientePldFields>[0]["onChange"] = (
    field,
    value,
  ) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setStatus("idle");
  };

  const save = async () => {
    setAttempted(true);
    setSaveError(null);
    // Validate what is actually sent, so a hidden field of the other kind never blocks saving.
    const payload = buildExpedienteSavePayload(draft);
    const { errors: blocking } = validateExpedienteForm(payload, true, {
      kind: translate("crm.contact_expediente.kind_required"),
      regime: translate("crm.contact_expediente.regime_required"),
      ownerName: translate("crm.contact_expediente.owner_name_required"),
      idExpired: "",
    });
    if (Object.keys(blocking).length > 0) return;
    setStatus("saving");
    try {
      await api.save(payload);
      setStatus("saved");
    } catch (error) {
      setStatus("idle");
      setSaveError(
        error instanceof Error
          ? error.message
          : translate("crm.contact_expediente.load_error"),
      );
    }
  };

  const fieldProps = { values: draft, messages, onChange };
  const kind = draft.person_kind;

  return (
    <form
      className="flex flex-col gap-6"
      noValidate
      autoComplete="off"
      data-contact-id={contactId}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <ExpedienteChecklist {...progress} />
      <ExpedienteRadioGroup<ExpedientePersonKind>
        legend={translate("crm.contact_expediente.kind_title")}
        name="person_kind"
        choices={EXPEDIENTE_PERSON_KINDS.map((value) => ({
          value,
          label: PERSON_KIND_LABELS[value],
        }))}
        selected={kind ?? null}
        message={messages.person_kind}
        onSelect={(value) => onChange("person_kind", value)}
      />
      {kind === "persona_moral" ? (
        <ExpedientePersonaMoralFields {...fieldProps} />
      ) : kind === "persona_fisica" ? (
        <ExpedientePersonaFisicaFields {...fieldProps} />
      ) : null}
      <ExpedientePldFields {...fieldProps} />
      <ExpedienteDocuments
        personKind={kind}
        isPep={draft.is_pep}
        documents={bundle.documents}
        actions={api}
      />
      <div className="flex flex-col gap-2">
        <p className="text-[15px] text-muted-foreground">
          {translate("crm.contact_expediente.save_hint")}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            className="h-11 sm:h-10"
            disabled={status === "saving"}
          >
            {translate(
              status === "saving"
                ? "crm.contact_expediente.saving"
                : "crm.contact_expediente.save",
            )}
          </Button>
          {status === "saved" ? (
            <span
              role="status"
              className="text-[15px] font-medium text-brand-green-text"
            >
              {translate("crm.contact_expediente.saved")}
            </span>
          ) : null}
        </div>
        {saveError ? (
          <p role="alert" className="text-[15px] text-brand-red-text">
            {saveError}
          </p>
        ) : null}
      </div>
    </form>
  );
};

const ExpedienteAudienceView = ({ contactId }: { contactId: Identifier }) => {
  const translate = useTranslate();
  const api = useContactExpediente(contactId);

  if (api.isPending) return <TabSkeleton />;
  if (api.error || !api.bundle) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="text-[15px]">
          {translate(
            isExpedienteDenied(api.error)
              ? "crm.contact_expediente.denied"
              : "crm.contact_expediente.load_error",
          )}
        </p>
        {isExpedienteDenied(api.error) ? null : (
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={api.retry}
          >
            {translate("crm.contact_expediente.retry")}
          </Button>
        )}
      </div>
    );
  }
  return <ExpedienteForm contactId={contactId} bundle={api.bundle} api={api} />;
};

/**
 * The "Expediente" tab of the contact file. Values, forms and documents render only for the five
 * audiences (`canAccess` right `contact_expediente`; the SQL/RLS stays the authority); every other
 * role that can open the contact sees only the read-only list of missing labels. It fetches only
 * when mounted, i.e. when the tab is opened.
 */
export const ContactExpedienteTab = ({
  contact,
}: {
  contact: Pick<Contact, "id" | "sales_id">;
}) => {
  // Only the id and the responsable go into the access check (and its query key).
  const { canAccess, isPending } = useCanAccess({
    resource: CONTACT_EXPEDIENTE_RESOURCE,
    action: "show",
    record: { id: contact.id, sales_id: contact.sales_id },
  });
  if (isPending) return <TabSkeleton />;
  return canAccess ? (
    <ExpedienteAudienceView contactId={contact.id} />
  ) : (
    <ExpedienteRestrictedView contactId={contact.id} />
  );
};
